import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { communityService, productService } from '../services/api';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import ImageCropper from '../components/ImageCropper';
import { readFile, validateImageFile } from '../utils/imageUtils';
import MyScheduleEditor from '../components/sedes/MyScheduleEditor';
import WeeklyScheduleCard from '../components/sedes/WeeklyScheduleCard';
import { buildGoMessagesPath } from '../utils/redirect';

// Shell top-level: si se define dentro del componente, cada render crea una
// referencia nueva y React desmonta+remonta el árbol — los <input> pierden
// foco al tipear.
function Shell({ theme, children }) {
  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/communities" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        {children}
      </main>
    </div>
  );
}

/**
 * @param scheduleCardPlacement
 *   'none' | 'header-right' | 'above-members' | 'above-products' | 'bottom-with-info'
 *   Define dónde se renderiza el WeeklyScheduleCard. Default = 'bottom-with-info'
 *   (al final de la página, junto a la card de Información, ocultando la card
 *   de Acciones rápidas). Variantes alternativas exposed en /communities1..3/:id.
 */
function CommunityViewNew({ scheduleCardPlacement = 'bottom-with-info' } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = getThemeVars(ACTIVE_THEME);

  const [community, setCommunity] = useState(null);
  const [products, setProducts] = useState([]);
  const [members, setMembers] = useState([]);

  const [membersCursor, setMembersCursor] = useState(null);
  const [membersHasMore, setMembersHasMore] = useState(false);
  const [membersLoadingMore, setMembersLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [joinLinkSubmitting, setJoinLinkSubmitting] = useState(false);
  const [shareLinkUrl, setShareLinkUrl] = useState(null); // url del modal de compartir, null = cerrado
  const [toast, setToast] = useState(null);
  const [joinLinkModal, setJoinLinkModal] = useState(false);
  const [joinLinkSingleUse, setJoinLinkSingleUse] = useState(false);
  const [joinLinkRequiresApproval, setJoinLinkRequiresApproval] = useState(true);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);

  const communityImageSrc = getImageUrl(community?.imageId);
  // El backend devuelve memberCount total via subquery COUNT en getCommunityById,
  // así que tenemos el total exacto sin necesidad de paginar la lista entera.
  // Fallback a members.length por si la card se renderea antes de que cargue
  // community (caso loading inicial).
  const totalMemberCount = community?.memberCount ?? members.length;
  const imageInputRef = React.useRef(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperFile, setCropperFile] = useState(null);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setToast({ type: 'error', message: validation.error });
      return;
    }
    const dataUrl = await readFile(file);
    setCropperFile(file);
    setCropperImage(dataUrl);
    setCropperOpen(true);
  };

  const handleCropSave = async (croppedFile) => {
    try {
      await communityService.uploadCommunityImage(id, croppedFile);
      await loadCommunityData();
      setToast({ type: 'success', message: 'Image updated' });
    } catch {
      setToast({ type: 'error', message: 'Failed to update image' });
    }
  };

  useEffect(() => { loadCommunityData(); }, [id, user]);

  useEffect(() => {
    if (user && community) checkMembership();
  }, [user, community]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadCommunityData = async () => {
    setLoading(true);
    try {
      const commResponse = await communityService.getCommunityById(id);
      const commData = commResponse.data;
      setCommunity(commData);

      if (user) {
        try {
          const productsResponse = await productService.getCommunityProductCards(id);
          setProducts(productsResponse.data || []);
        } catch {
          setProducts([]);
        }
      } else {
        setProducts([]);
      }

      try {
        const membersResponse = await communityService.getCommunityMembers(id, { pageSize: 20 });
        // membersResponse.data is already unwrapped by the Axios interceptor
        const payload = membersResponse.data;
        // Handle: plain array [], {items:[]}, or {data:[]}
        const items = Array.isArray(payload) ? payload : (payload?.items ?? payload?.data ?? []);
        setMembers(items);
        setMembersCursor(payload?.nextCursor ?? null);
        setMembersHasMore(payload?.hasMore ?? false);

      } catch {
        setMembers([]);
      }
    } catch {
      // community will remain null
    } finally {
      setLoading(false);
    }
  };

  const checkMembership = async () => {
    if (!user || !community) return;
    const role = community.currentUserRole;
    setIsCreator(role === 'CREATOR' || community.creatorId === user.id);
    const adminByRole = role === 'CREATOR' || role === 'ADMIN';
    setIsAdmin(adminByRole || community.creatorId === user.id);
    try {
      const response = await communityService.checkMembership(id, user.id);
      const data = response.data;
      setIsMember(data.isMember || false);
    } catch {
      setIsMember(false);
    }
  };

  // Al cambiar membresía hay que invalidar también el feed del dashboard
  // (productCards) y la actividad reciente — si no, al volver a la home se
  // siguen viendo los productos cacheados de antes y hay que refrescar a mano.
  const invalidateMembershipDependentQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['userCommunities'] });
    queryClient.invalidateQueries({ queryKey: ['availableCommunities'] });
    queryClient.invalidateQueries({ queryKey: ['productCards'] });
    queryClient.invalidateQueries({ queryKey: ['recentActivity'] });
  };

  const handleJoinCommunity = async () => {
    try {
      await communityService.joinCommunity(id, user.id);
      setIsMember(true);
      loadCommunityData();
      invalidateMembershipDependentQueries();
    } catch {
      setToast({ type: 'error', text: t('Error al unirse a la comunidad') });
    }
  };

  const handleLeaveCommunity = async () => {
    if (!window.confirm(t('¿Estás seguro de que quieres salir de esta comunidad?'))) return;
    try {
      await communityService.leaveCommunity(id, user.id);
      invalidateMembershipDependentQueries();
      navigate('/communities', { replace: true });
    } catch {
      setToast({ type: 'error', text: t('Error al salir de la comunidad') });
    }
  };

  const handleInviteMember = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteSubmitting(true);
    try {
      await communityService.createInvitation(id, email, inviteMessage.trim() || null);
      setInviteDialog(false);
      setInviteEmail('');
      setInviteMessage('');
      setToast({ type: 'success', text: t('Invitación enviada exitosamente') });
    } catch (error) {
      setToast({ type: 'error', text: error?.response?.data?.message || t('Error al enviar la invitación') });
    } finally {
      setInviteSubmitting(false);
    }
  };

  const copyToClipboard = async (value) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return;
    }
    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  };

  const openJoinLinkFlow = () => {
    if (isAdmin) {
      setJoinLinkSingleUse(false);
      setJoinLinkRequiresApproval(community?.linkJoinRequiresApproval ?? true);
      setJoinLinkModal(true);
    } else {
      handleCreateJoinLink({});
    }
  };

  const handleCreateJoinLink = async ({ maxUses, requiresApproval } = {}) => {
    setJoinLinkSubmitting(true);
    try {
      const response = await communityService.createJoinLink(id, {
        ...(maxUses != null ? { maxUses } : {}),
        ...(requiresApproval != null ? { requiresApproval } : {}),
      });
      const joinLink = response?.data;
      const token = joinLink?.token;
      if (!token) throw new Error(t('No se pudo generar el enlace de ingreso'));
      const joinUrl = `${window.location.origin}/join?token=${encodeURIComponent(token)}`;
      // Auto-copia al portapapeles + abrir modal de compartir. Antes mostrábamos
      // sólo un toast "copiado" pero el user no veía el link y no podía
      // mandarlo por WhatsApp/Gmail directo.
      try { await copyToClipboard(joinUrl); } catch (_) { /* fallback ok */ }
      setShareLinkUrl(joinUrl);
    } catch (error) {
      setToast({ type: 'error', text: error?.response?.data?.message || error?.message || t('Error al generar el enlace de ingreso') });
    } finally {
      setJoinLinkSubmitting(false);
      setJoinLinkModal(false);
    }
  };

  // Mensaje predefinido para los share buttons. La comunidad name es opcional
  // (puede no estar cargada todavía cuando el modal se abre por race con un
  // refresh). El link va siempre al final para que cualquier preview lo agarre.
  const buildShareMessage = (url) => {
    const name = community?.name?.trim();
    return name
      ? t('Te invito a unirte a {{name}} en Vatuco: {{url}}', { name, url })
      : t('Te invito a unirte a mi comunidad en Vatuco: {{url}}', { url });
  };

  const shareNative = async (url) => {
    if (!navigator.share) return false;
    try {
      await navigator.share({
        title: community?.name ? t('Invitación a {{name}}', { name: community.name }) : t('Invitación a Vatuco'),
        text: buildShareMessage(url),
        url,
      });
      return true;
    } catch (_) {
      return false;
    }
  };


  const loadMoreMembers = async () => {
    if (!membersCursor || membersLoadingMore) return;
    setMembersLoadingMore(true);
    try {
      const response = await communityService.getCommunityMembers(id, { pageSize: 20, cursor: membersCursor });
      // response.data is already unwrapped by the Axios interceptor
      const payload = response.data;
      // Handle: plain array [], {items:[]}, or {data:[]}
      const newMembers = Array.isArray(payload) ? payload : (payload?.items ?? payload?.data ?? []);
      setMembers((prev) => [...prev, ...newMembers]);
      setMembersCursor(payload?.nextCursor ?? null);
      setMembersHasMore(payload?.hasMore ?? false);
    } catch { /* ignore */ }
    finally { setMembersLoadingMore(false); }
  };

  // ── Loading skeleton ──
  if (loading) {
    return (
      <Shell theme={theme}>
        <div className="px-4 mobile-sm:px-6 tablet-sm:px-8 pt-5 tablet-sm:pt-8 pb-24 tablet-sm:pb-12">
          <div className="flex flex-col md:flex-row gap-10 items-start">
            <div className="skeleton w-48 h-48 rounded-full shrink-0" />
            <div className="flex-1 space-y-4 pt-4">
              <div className="skeleton h-10 w-80 rounded" />
              <div className="skeleton h-5 w-96 rounded" />
              <div className="skeleton h-20 w-[500px] rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <div key={i} className="bg-surface-container rounded-xl overflow-hidden">
                    <div className="skeleton h-44 w-full" />
                    <div className="p-4 space-y-2">
                      <div className="skeleton h-4 w-3/4 rounded" />
                      <div className="skeleton h-3 w-1/2 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-3">
              <div className="skeleton h-80 w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // ── Community not found ──
  if (!community) {
    return (
      <Shell theme={theme}>
        <div className="px-4 mobile-sm:px-6 tablet-sm:px-8 pt-8 tablet-sm:pt-12 pb-24 tablet-sm:pb-12">
          <div className="flex items-center gap-3 p-4 rounded-lg bg-error-container text-on-error-container">
            <span className="material-symbols-outlined">error</span>
            {t('Comunidad no encontrada')}
          </div>
          <button
            onClick={() => navigate('/communities')}
            className="mt-4 px-6 py-2.5 rounded-lg bg-primary text-on-primary font-medium hover:opacity-90 transition-opacity"
          >
            {t('Volver a Comunidades')}
          </button>
        </div>
      </Shell>
    );
  }

  const previewProducts = products.slice(0, 5);

  const handleBrowseProducts = () => {
    navigate(`/?community=${encodeURIComponent(community.name)}`);
  };

  return (
    <Shell theme={theme}>
      <div className="px-4 mobile-sm:px-6 tablet-sm:px-8 pt-5 tablet-sm:pt-8 pb-24 tablet-sm:pb-12 max-w-6xl mx-auto space-y-8">

        {/* ── COMMUNITY HEADER (optionally wrapped with schedule card) ──
             Solo aplicamos el grid 2-col cuando la comunidad tiene horarios
             habilitados — sino la 2da columna queda vacía y deforma la página. */}
        <div className={scheduleCardPlacement === 'header-right' && isMember && community?.scheduleEnabled
          ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-4 items-stretch'
          : ''}>
        <header className="bg-surface-container rounded-2xl p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-center">
            {/* Community image — click to change if creator */}
            <div
              className={`relative group shrink-0 ${isCreator ? 'cursor-pointer' : ''}`}
              onClick={() => isCreator && imageInputRef.current?.click()}
            >
              {communityImageSrc ? (
                <img
                  src={communityImageSrc}
                  alt={community.name}
                  className="w-20 h-20 rounded-2xl object-cover border-2 border-outline-variant/10 shadow-sm"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-primary-container border-2 border-outline-variant/10 shadow-sm flex items-center justify-center">
                  <span className="text-3xl font-black text-on-primary-container">
                    {community.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )}
              {isCreator && (
                <>
                  <div className="absolute inset-0 rounded-2xl bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <span className="material-symbols-outlined text-white text-xl opacity-0 group-hover:opacity-100 transition-opacity">photo_camera</span>
                  </div>
                  <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                </>
              )}
            </div>

            {/* Name + description + stats */}
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tight leading-tight break-words">
                {community.name}
              </h1>
              {community.description && (
                <p className="text-sm text-on-surface-variant mt-1 max-w-xl break-words">{community.description}</p>
              )}
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-3 mt-3">
                <span className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                  <span className="material-symbols-outlined text-[14px] text-primary">group</span>
                  <span className="font-bold text-on-surface">{totalMemberCount}</span> {t('miembros')}
                </span>
                <span className="w-px h-3.5 bg-outline-variant/30 hidden sm:block" />
                <span className="flex items-center gap-1.5 text-xs text-on-surface-variant">
                  <span className="material-symbols-outlined text-[14px] text-secondary">storefront</span>
                  <span className="font-bold text-on-surface">{products.length}</span> {t('productos')}
                </span>
                {community.domain && (
                  <>
                    <span className="w-px h-3.5 bg-outline-variant/30 hidden sm:block" />
                    <span className="px-2 py-0.5 bg-secondary-container text-on-secondary-container rounded-full text-[9px] font-bold uppercase tracking-widest">
                      {community.domain}
                    </span>
                  </>
                )}
                {community.allowDomainJoin && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-container text-on-primary-container rounded-full text-[9px] font-bold uppercase tracking-widest">
                    <span className="material-symbols-outlined text-[10px]">lock_open</span>
                    {t('Unirse por dominio')}
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons — 2x2 grid on desktop */}
            <div className="grid grid-cols-2 gap-2 shrink-0 w-full sm:w-auto">
              {user && !isMember && !isCreator && (
                <button onClick={handleJoinCommunity} className="col-span-2 inline-flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-primary text-on-primary font-bold text-sm hover:opacity-90 transition-opacity">
                  <span className="material-symbols-outlined text-[16px]">add</span>
                  {t('Unirse')}
                </button>
              )}
              {isCreator && (
                <button onClick={() => navigate(`/communities/${id}/edit`)} className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl border border-outline-variant/30 text-on-surface font-medium text-xs hover:bg-surface-container-high/50 transition-colors">
                  <span className="material-symbols-outlined text-[14px]">edit</span>
                  {t('Editar')}
                </button>
              )}
              {isAdmin && (
                <button onClick={() => setInviteDialog(true)} className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-primary text-on-primary font-medium text-xs hover:opacity-90 transition-opacity">
                  <span className="material-symbols-outlined text-[14px]">person_add</span>
                  {t('Invitar')}
                </button>
              )}
              {isAdmin && (
                <button onClick={openJoinLinkFlow} disabled={joinLinkSubmitting} className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl border border-outline-variant/30 text-on-surface font-medium text-xs hover:bg-surface-container-high/50 transition-colors disabled:opacity-50">
                  <span className="material-symbols-outlined text-[14px]">link</span>
                  {t('Enlace')}
                </button>
              )}
              {isAdmin && (
                <button onClick={() => navigate(`/communities/${id}/admin`)} className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-secondary-container text-on-secondary-container font-medium text-xs hover:opacity-90 transition-opacity">
                  <span className="material-symbols-outlined text-[14px]">admin_panel_settings</span>
                  {t('Admin Dashboard')}
                </button>
              )}
              {/* "Mis horarios" se sacó del header — ahora vive en la card
                  inferior (WeeklyScheduleCard / placeholder) que se renderea
                  abajo cuando la comunidad tiene scheduleEnabled. */}
            </div>
          </div>
        </header>
        {scheduleCardPlacement === 'header-right' && isMember && community?.scheduleEnabled && (
          <WeeklyScheduleCard
            communityId={id}
            onEditClick={() => setScheduleEditorOpen(true)}
            compact
            className="h-full"
          />
        )}
        </div>

        {community && (
          <MyScheduleEditor
            open={scheduleEditorOpen}
            communityId={id}
            communityName={community.name}
            onClose={() => setScheduleEditorOpen(false)}
            onToast={(type, message) => setToast({ type, message })}
          />
        )}

        {/* ── CARD GRID ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Products column ────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">
          {scheduleCardPlacement === 'above-products' && isMember && community?.scheduleEnabled && (
            <WeeklyScheduleCard
              communityId={id}
              onEditClick={() => setScheduleEditorOpen(true)}
            />
          )}

          {/* ── Products Card ──────────────────────────────────────── */}
          <div className="bg-surface-container rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 pb-0">
              <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-[20px] text-primary">storefront</span>
                {t('Productos')}
              </h2>
              {products.length > 0 && (
                <button onClick={handleBrowseProducts} className="text-xs font-bold text-primary hover:underline flex items-center gap-1">
                  {t('Ver todos')}
                  <span className="material-symbols-outlined text-[14px]">arrow_forward</span>
                </button>
              )}
            </div>

            {products.length === 0 ? (
              <div className="px-6 py-10 text-center flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-primary-container/40 flex items-center justify-center mb-4">
                  <span className="material-symbols-outlined text-[40px] text-primary">storefront</span>
                </div>
                <h3 className="text-base font-bold text-on-surface mb-1">
                  {t('Aún no hay productos')}
                </h3>
                <p className="text-sm text-on-surface-variant max-w-sm">
                  {isMember
                    ? t('Sé el primero en publicar un producto y dar vida al marketplace de esta comunidad.')
                    : t('Cuando los miembros publiquen productos vas a verlos acá.')}
                </p>
                {isMember && (
                  <button onClick={() => navigate('/create-product')} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-on-primary font-bold text-sm hover:opacity-90 transition-opacity shadow-sm">
                    <span className="material-symbols-outlined text-[18px]">add</span>
                    {t('Publicar primer producto')}
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y divide-outline-variant/10">
                {previewProducts.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => navigate(`/products/${product.id}`)}
                    className="flex items-center gap-4 px-5 py-3.5 hover:bg-surface-container-high/40 transition-colors cursor-pointer group"
                  >
                    {product.imageId ? (
                      <img src={getImageUrl(product.imageId)} alt="" className="w-12 h-12 rounded-xl object-cover bg-surface-container-highest shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-surface-container-highest flex items-center justify-center shrink-0">
                        <span className="material-symbols-outlined text-on-surface-variant/40">image</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-on-surface truncate group-hover:text-primary transition-colors">{product.name}</p>
                      <p className="text-xs text-on-surface-variant mt-0.5">{product.publisherName || t('Miembro')}</p>
                    </div>
                    {product.price != null && (
                      <span className="text-sm font-black text-primary shrink-0">${product.price}</span>
                    )}
                    <span className="material-symbols-outlined text-[18px] text-on-surface-variant/30 group-hover:text-primary transition-colors shrink-0">chevron_right</span>
                  </div>
                ))}
                {products.length > 5 && (
                  <div className="p-4 text-center">
                    <button onClick={handleBrowseProducts} className="text-xs font-bold text-primary hover:underline">
                      {t('Ver los')} {products.length} {t('productos')} &rarr;
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          </div>{/* /Products column */}

          {scheduleCardPlacement === 'above-members' && isMember && community?.scheduleEnabled && (
            <WeeklyScheduleCard
              communityId={id}
              onEditClick={() => setScheduleEditorOpen(true)}
              compact
            />
          )}

          {/* ── Members Card ──────────────────────────────────────── */}
          <div className="bg-surface-container rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-5 pb-0">
              <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-[20px] text-primary">group</span>
                {t('Miembros')}
              </h2>
              <span className="text-xs font-bold text-on-surface-variant bg-surface-container-highest px-2.5 py-1 rounded-full">{totalMemberCount}</span>
            </div>

            {members.length === 0 ? (
              <p className="text-sm text-on-surface-variant text-center py-6 px-5">{t('No hay miembros para mostrar')}</p>
            ) : (
              <div className="divide-y divide-outline-variant/10 mt-4">
                {members.slice(0, 6).map((member) => (
                  <div key={member.id} onClick={() => navigate(`/users/${member.id}`)} className="flex items-center gap-3 cursor-pointer group/member px-5 py-3 hover:bg-surface-container-high/40 transition-colors">
                    <div className="relative shrink-0">
                      {member.profilePictureId ? (
                        <img src={getImageUrl(member.profilePictureId)} alt={member.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary-container flex items-center justify-center">
                          <span className="text-xs font-bold text-on-primary-container">{member.name?.charAt(0)?.toUpperCase()}</span>
                        </div>
                      )}
                      {member.id === community.creatorId && (
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-primary rounded-full border-2 border-surface-container flex items-center justify-center">
                          <span className="material-symbols-outlined text-[8px] text-on-primary" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-on-surface font-medium truncate group-hover/member:text-primary transition-colors">{member.name}</p>
                      <span className="text-[9px] text-on-surface-variant/60 uppercase font-bold tracking-widest">
                        {member.id === community.creatorId ? t('Creador') : t('Miembro')}
                      </span>
                    </div>
                  </div>
                ))}
                {totalMemberCount > 6 && (
                  <div className="px-5 py-3 text-center">
                    <button onClick={() => setMembersModalOpen(true)} className="text-xs font-bold text-primary hover:underline">
                      {t('Ver los')} {totalMemberCount} {t('miembros')} &rarr;
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="px-5 pb-5 pt-3 space-y-2">
              {user && isMember && !isCreator && (
                <button onClick={handleLeaveCommunity} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-outline-variant/20 text-on-surface-variant text-xs font-bold hover:border-error/40 hover:text-error hover:bg-error/5 transition-colors">
                  <span className="material-symbols-outlined text-[16px]">logout</span>
                  {t('Salir de la comunidad')}
                </button>
              )}
            </div>
          </div>

          {/* ── Info Card ─────────────────────────────────────────── */}
          <div className="bg-surface-container rounded-2xl p-5">
            <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2 mb-4">
              <span className="material-symbols-outlined text-[20px] text-primary">info</span>
              {t('Información')}
            </h2>
            <div className="space-y-4">
              {community.domain && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-secondary-container/50 flex items-center justify-center shrink-0">
                    <span className="material-symbols-outlined text-[18px] text-on-secondary-container">domain</span>
                  </div>
                  <div>
                    <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-wider">{t('Dominio')}</p>
                    <p className="text-sm font-medium text-on-surface">{community.domain}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary-container/50 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[18px] text-on-primary-container">{community.allowDomainJoin ? 'lock_open' : 'lock'}</span>
                </div>
                <div>
                  <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-wider">{t('Acceso')}</p>
                  <p className="text-sm font-medium text-on-surface">{community.allowDomainJoin ? t('Abierta por dominio') : t('Solo por invitación')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-tertiary-container/50 flex items-center justify-center shrink-0">
                  <span className="material-symbols-outlined text-[18px] text-on-tertiary-container">send</span>
                </div>
                <div>
                  <p className="text-[10px] text-on-surface-variant uppercase font-bold tracking-wider">{t('Invitaciones')}</p>
                  <p className="text-sm font-medium text-on-surface">
                    {community.invitePolicy === 'ADMIN_ONLY' ? t('Solo el creador puede invitar') :
                     t('Miembros pueden invitar')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Schedule Card (variant D: bottom, next to Info) ──── */}
          {scheduleCardPlacement === 'bottom-with-info' && isMember && community?.scheduleEnabled && (
            <WeeklyScheduleCard
              communityId={id}
              onEditClick={() => setScheduleEditorOpen(true)}
              className="lg:col-span-2"
            />
          )}

          {/* ── Quick Actions Card (for members) — hidden in variant D ─ */}
          {isMember && scheduleCardPlacement !== 'bottom-with-info' && (
            <div className="lg:col-span-2 bg-surface-container rounded-2xl p-5">
              <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-[20px] text-primary">bolt</span>
                {t('Acciones rápidas')}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => navigate('/create-product')} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-primary-container/30 hover:bg-primary-container/70 hover:shadow-sm transition-colors group">
                  <span className="material-symbols-outlined text-2xl text-primary">add_circle</span>
                  <span className="text-xs font-bold text-on-surface">{t('Publicar')}</span>
                </button>
                <button onClick={handleBrowseProducts} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-secondary-container/30 hover:bg-secondary-container/70 hover:shadow-sm transition-colors group">
                  <span className="material-symbols-outlined text-2xl text-secondary">search</span>
                  <span className="text-xs font-bold text-on-surface">{t('Buscar')}</span>
                </button>
                {community?.invitePolicy === 'OPEN' && (
                  <>
                    <button onClick={() => setInviteDialog(true)} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-tertiary-container/30 hover:bg-tertiary-container/70 hover:shadow-sm transition-colors group">
                      <span className="material-symbols-outlined text-2xl text-tertiary">mail</span>
                      <span className="text-xs font-bold text-on-surface">{t('Invitar')}</span>
                    </button>
                    <button onClick={openJoinLinkFlow} disabled={joinLinkSubmitting} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-tertiary-container/30 hover:bg-tertiary-container/70 hover:shadow-sm transition-colors group disabled:opacity-50">
                      <span className="material-symbols-outlined text-2xl text-tertiary">link</span>
                      <span className="text-xs font-bold text-on-surface">{joinLinkSubmitting ? t('Generando...') : t('Compartir')}</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── Quick Actions Card (for creator) — hidden in variant D ─ */}
          {isCreator && !isMember && scheduleCardPlacement !== 'bottom-with-info' && (
            <div className="lg:col-span-2 bg-surface-container rounded-2xl p-5">
              <h2 className="text-lg font-bold tracking-tight text-on-surface flex items-center gap-2 mb-4">
                <span className="material-symbols-outlined text-[20px] text-primary">bolt</span>
                {t('Acciones rápidas')}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setInviteDialog(true)} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-primary-container/30 hover:bg-primary-container/70 hover:shadow-sm transition-colors group">
                  <span className="material-symbols-outlined text-2xl text-primary">mail</span>
                  <span className="text-xs font-bold text-on-surface">{t('Invitar')}</span>
                </button>
                <button onClick={openJoinLinkFlow} disabled={joinLinkSubmitting} className="flex flex-col items-center gap-2 p-4 rounded-xl bg-secondary-container/30 hover:bg-secondary-container/70 hover:shadow-sm transition-colors group disabled:opacity-50">
                  <span className="material-symbols-outlined text-2xl text-secondary">link</span>
                  <span className="text-xs font-bold text-on-surface">{joinLinkSubmitting ? t('Generando...') : t('Compartir')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── INVITE MODAL ──────────────────────────────────────────── */}
      {inviteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setInviteDialog(false)} />
          <div className="relative bg-surface-container-lowest rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-on-surface">{t('Invitar Miembro')}</h2>
              <button
                onClick={() => setInviteDialog(false)}
                className="w-8 h-8 rounded-full hover:bg-surface-container flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                {t('Email')}
              </label>
              <input
                type="email"
                autoFocus
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border-2 border-outline-variant/30 bg-transparent text-on-surface placeholder:text-on-surface-variant/50"
                placeholder="user@example.com"
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-on-surface-variant mb-1.5">
                {t('Mensaje (opcional)')}
              </label>
              <textarea
                rows={3}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg border-2 border-outline-variant/30 bg-transparent text-on-surface placeholder:text-on-surface-variant/50 resize-none"
                placeholder={t('Añade un mensaje personalizado a la invitación...')}
              />
            </div>

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setInviteDialog(false)}
                disabled={inviteSubmitting}
                className="px-5 py-2.5 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors disabled:opacity-50"
              >
                {t('Cancelar')}
              </button>
              <button
                onClick={handleInviteMember}
                disabled={!inviteEmail.trim() || inviteSubmitting}
                className="px-5 py-2.5 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {inviteSubmitting ? t('Enviando...') : t('Enviar Invitación')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Members Modal ────────────────────────────────────────── */}
      {membersModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMembersModalOpen(false)} />
          <div className="relative bg-surface-container-lowest rounded-2xl max-w-md w-full mx-4 shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-outline-variant/10 shrink-0">
              <h2 className="text-lg font-bold text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-[20px] text-primary">group</span>
                {t('Miembros')} ({totalMemberCount})
              </h2>
              <button onClick={() => setMembersModalOpen(false)} className="w-8 h-8 rounded-full hover:bg-surface-container flex items-center justify-center">
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-outline-variant/10">
              {members.map((member) => (
                <div
                  key={member.id}
                  onClick={() => { setMembersModalOpen(false); navigate(`/users/${member.id}`); }}
                  className="flex items-center gap-3 px-5 py-3.5 cursor-pointer hover:bg-surface-container-high/40 transition-colors group/member"
                >
                  <div className="relative shrink-0">
                    {member.profilePictureId ? (
                      <img src={getImageUrl(member.profilePictureId)} alt={member.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center">
                        <span className="text-sm font-bold text-on-primary-container">{member.name?.charAt(0)?.toUpperCase()}</span>
                      </div>
                    )}
                    {member.id === community.creatorId && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-primary rounded-full border-2 border-surface-container-lowest flex items-center justify-center">
                        <span className="material-symbols-outlined text-[8px] text-on-primary" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-on-surface font-medium truncate group-hover/member:text-primary transition-colors">{member.name}</p>
                    <span className="text-[10px] text-on-surface-variant/60 uppercase font-bold tracking-widest">
                      {member.id === community.creatorId ? t('Creador') : t('Miembro')}
                    </span>
                  </div>
                  {member.id !== user?.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMembersModalOpen(false);
                        navigate(buildGoMessagesPath({ sellerId: member.id }));
                      }}
                      className="w-9 h-9 rounded-lg hover:bg-primary-container/40 text-primary flex items-center justify-center transition-colors"
                      title={t('Mandar mensaje a {{name}}', { name: member.name })}
                      aria-label={t('Mandar mensaje a {{name}}', { name: member.name })}
                    >
                      <span className="material-symbols-outlined text-[20px]">forum</span>
                    </button>
                  )}
                  <span className="material-symbols-outlined text-[18px] text-on-surface-variant/30 group-hover/member:text-primary transition-colors">chevron_right</span>
                </div>
              ))}
              {membersHasMore && (
                <div className="px-5 py-3 text-center">
                  <button
                    onClick={loadMoreMembers}
                    disabled={membersLoadingMore}
                    className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
                  >
                    {membersLoadingMore ? t('Cargando...') : t('Cargar más')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Join Link Modal (admin) ──────────────────────────────── */}
      {joinLinkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setJoinLinkModal(false)} />
          <div className="relative bg-surface-container-lowest rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-on-surface">{t('¿Crear link de invitación?')}</h2>
              <button
                onClick={() => setJoinLinkModal(false)}
                className="w-8 h-8 rounded-full hover:bg-surface-container flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-[20px]">close</span>
              </button>
            </div>

            {/* Usos */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">{t('Usos')}</p>
              <div className="flex gap-2">
                {[
                  { val: false, label: t('Usos ilimitados') },
                  { val: true, label: t('Un solo uso') },
                ].map(({ val, label }) => (
                  <button
                    key={String(val)}
                    onClick={() => setJoinLinkSingleUse(val)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                      joinLinkSingleUse === val
                        ? 'bg-primary text-on-primary border-primary'
                        : 'text-on-surface-variant border-outline-variant/30 hover:bg-surface-container-high/50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Aprobación — solo admins eligen; los miembros usan el default de la comunidad */}
            {isAdmin && (
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <span className="relative inline-flex shrink-0">
                    <input
                      type="checkbox"
                      checked={joinLinkRequiresApproval}
                      onChange={(e) => setJoinLinkRequiresApproval(e.target.checked)}
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className="w-5 h-5 rounded-md border-2 border-on-surface-variant bg-surface flex items-center justify-center transition-colors peer-checked:bg-primary peer-checked:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-primary/50"
                    >
                      <span
                        className={`material-symbols-outlined text-on-primary text-[16px] leading-none ${joinLinkRequiresApproval ? 'opacity-100' : 'opacity-0'}`}
                      >
                        check
                      </span>
                    </span>
                  </span>
                  <span className="text-sm text-on-surface">{t('Requiere aprobación de admin')}</span>
                </label>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setJoinLinkModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-on-surface-variant hover:bg-surface-container transition-colors"
              >
                {t('Cancelar')}
              </button>
              <button
                onClick={() => handleCreateJoinLink({
                  maxUses: joinLinkSingleUse ? 1 : null,
                  requiresApproval: joinLinkRequiresApproval,
                })}
                disabled={joinLinkSubmitting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-primary text-on-primary hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {joinLinkSubmitting ? t('Generando...') : t('Crear link')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de compartir el link ya generado ─────────────────────
          Aparece después de generar el link (para admins o miembros).
          Muestra el link visible, un botón Copiar (el clipboard ya tiene
          el valor — el botón sirve como confirmación + fallback) y los
          botones de WhatsApp / Gmail / Telegram / share nativo. */}
      {shareLinkUrl && (() => {
        const message = buildShareMessage(shareLinkUrl);
        const subject = community?.name
          ? t('Invitación a {{name}} en Vatuco', { name: community.name })
          : t('Invitación a Vatuco');
        const wa  = `https://wa.me/?text=${encodeURIComponent(message)}`;
        const mail = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
        // Logos inline en color de marca, sin label ni fondo coloreado: sólo
        // el ícono. Se renderizan como botones circulares chicos.
        const WhatsAppIcon = (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
            <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807a9.86 9.86 0 0 0 5.024 1.378h.005c5.448 0 9.886-4.434 9.889-9.885A9.825 9.825 0 0 0 18.685 4.7a9.821 9.821 0 0 0-6.984-2.892c-5.452 0-9.887 4.434-9.889 9.884a9.866 9.866 0 0 0 1.512 5.26l-.999 3.648 3.742-.981.587.314zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479s1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/>
          </svg>
        );
        const GmailIcon = (
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
            <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
          </svg>
        );
        const shareButtons = [
          { id: 'whatsapp', label: 'WhatsApp', svg: WhatsAppIcon, color: '#25D366', href: wa },
          { id: 'gmail',    label: 'Email',    svg: GmailIcon,    color: '#EA4335', href: mail },
        ];
        const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShareLinkUrl(null)} />
            <div className="relative bg-surface-container-lowest rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-bold text-on-surface flex items-center gap-2">
                    <span className="material-symbols-outlined text-[20px] text-primary">link</span>
                    {t('Link de invitación listo')}
                  </h2>
                  {community?.name && (
                    <p className="text-xs text-on-surface-variant mt-0.5">
                      {t('Para sumar gente a {{name}}', { name: community.name })}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setShareLinkUrl(null)}
                  className="w-8 h-8 rounded-full hover:bg-surface-container flex items-center justify-center"
                  aria-label={t('Cerrar')}
                >
                  <span className="material-symbols-outlined text-[20px]">close</span>
                </button>
              </div>

              <p className="text-[11px] text-on-surface-variant mb-2 inline-flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px] text-primary">check_circle</span>
                {t('Ya lo copiamos al portapapeles')}
              </p>

              {/* Input read-only con el link + botón copiar */}
              <div className="flex items-center gap-2 mb-5">
                <input
                  type="text"
                  value={shareLinkUrl}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg bg-surface-container text-on-surface text-xs border border-outline-variant/30 focus:outline-none focus:ring-2 focus:ring-primary/40 truncate"
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await copyToClipboard(shareLinkUrl);
                      setToast({ type: 'success', text: t('Copiado al portapapeles') });
                    } catch (_) {
                      setToast({ type: 'error', text: t('No se pudo copiar el link') });
                    }
                  }}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold bg-primary text-on-primary hover:opacity-90 transition-opacity"
                >
                  <span className="material-symbols-outlined text-[16px]">content_copy</span>
                  {t('Copiar')}
                </button>
              </div>

              {/* Botones de share — sólo logo, circular, color de marca. */}
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold uppercase tracking-widest text-on-surface-variant">
                  {t('Compartir por')}
                </span>
                {shareButtons.map(b => (
                  <a
                    key={b.id}
                    href={b.href}
                    target={b.id === 'gmail' ? undefined : '_blank'}
                    rel="noopener noreferrer"
                    title={b.label}
                    aria-label={b.label}
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                    style={{ color: b.color }}
                  >
                    {b.svg}
                  </a>
                ))}
                {canNativeShare && (
                  <button
                    type="button"
                    onClick={() => shareNative(shareLinkUrl)}
                    title={t('Más opciones')}
                    aria-label={t('Más opciones')}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-on-surface-variant hover:text-on-surface hover:bg-surface-container transition-colors ml-auto"
                  >
                    <span className="material-symbols-outlined text-[20px]">share</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Toast ─────────────────────────────────────────────────── */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-medium animate-fade-in ${
          toast.type === 'error'
            ? 'bg-error-container text-on-error-container'
            : 'bg-on-surface text-surface'
        }`}>
          {toast.text}
        </div>
      )}
      {/* Image cropper dialog */}
      <ImageCropper
        open={cropperOpen}
        image={cropperImage}
        originalFile={cropperFile}
        cropShape="round"
        onClose={() => setCropperOpen(false)}
        onSave={handleCropSave}
      />
    </Shell>
  );
}

export default CommunityViewNew;
